import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaToggleOff, FaToggleOn, FaSpinner, FaTimes, FaSave } from 'react-icons/fa';
import Button from '../../components/Button';
import Table from '../../components/Table';
import Input from '../../components/Input';
import PageHeader from '../../components/PageHeader';
import Skeleton from '../../components/Skeleton';
import { planService } from '../../services/planService';
import { toastSuccess, toastError } from '../../utils/toast';
import { swalConfirm } from '../../utils/swal';

const EMPTY_FORM = { name: '', text_limit_weekly: '', voice_limit_weekly: '' };

const PlanModal = ({ isOpen, editingPlan, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(editingPlan
      ? {
          name: editingPlan.name,
          text_limit_weekly: String(editingPlan.text_limit_weekly ?? ''),
          voice_limit_weekly: String(editingPlan.voice_limit_weekly ?? ''),
        }
      : EMPTY_FORM
    );
    setErrors({});
  }, [isOpen, editingPlan]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Requerido';
    if (!form.text_limit_weekly || isNaN(form.text_limit_weekly) || Number(form.text_limit_weekly) < 0)
      e.text_limit_weekly = 'Número válido requerido';
    if (!form.voice_limit_weekly || isNaN(form.voice_limit_weekly) || Number(form.voice_limit_weekly) < 0)
      e.voice_limit_weekly = 'Número válido requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        text_limit_weekly: parseInt(form.text_limit_weekly, 10),
        voice_limit_weekly: parseInt(form.voice_limit_weekly, 10),
      };
      if (editingPlan) {
        await planService.update(editingPlan.id, payload);
        toastSuccess('Plan actualizado');
      } else {
        await planService.create(payload);
        toastSuccess('Plan creado');
      }
      onSaved();
      onClose();
    } catch (err) {
      toastError(err.message || 'Error al guardar plan');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-surface-card border border-white/[0.06] rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.07]">
          <h2 className="font-semibold" style={{ color: '#E8EAF0' }}>{editingPlan ? 'Editar plan' : 'Nuevo plan'}</h2>
          <button onClick={onClose} style={{ color: '#4A5270' }} className="hover:text-white transition-colors"><FaTimes size={16} /></button>
        </div>
        <div className="p-5 space-y-2">
          <Input
            label="Nombre del plan *"
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            error={errors.name}
          />
          <Input
            label="Límite texto/semana *"
            type="number"
            min="0"
            placeholder="Ej: 750"
            value={form.text_limit_weekly}
            onChange={e => setForm(p => ({ ...p, text_limit_weekly: e.target.value }))}
            error={errors.text_limit_weekly}
          />
          <Input
            label="Límite voz/semana *"
            type="number"
            min="0"
            placeholder="Ej: 125"
            value={form.voice_limit_weekly}
            onChange={e => setForm(p => ({ ...p, voice_limit_weekly: e.target.value }))}
            error={errors.voice_limit_weekly}
          />
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/[0.07]">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminPlanesPage = () => {
  const [plans, setPlans] = useState([]);
  const [userCounts, setUserCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const [data, counts] = await Promise.all([
        planService.getAll(),
        planService.getUserCounts(),
      ]);
      setPlans(data);
      setUserCounts(counts);
    } catch (err) {
      toastError(err.message || 'Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleDeactivate = async (plan) => {
    const { isConfirmed } = await swalConfirm(
      `¿Desactivar "${plan.name}"?`,
      'Los usuarios con este plan conservarán los límites que tenían. Solo admins pueden desactivar planes.',
      'Sí, desactivar',
      { input: null, html: null, footer: null, showDenyButton: false }
    );
    if (!isConfirmed) return;
    try {
      await planService.deactivate(plan.id);
      toastSuccess('Plan desactivado');
      fetchPlans();
    } catch (err) {
      toastError(err.message || 'Error al desactivar plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const columns = [
    {
      key: 'name',
      title: 'Nombre',
      sortable: true,
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: '#E8EAF0' }}>{v}</span>
          {!row.is_active && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: '#9BA3B8' }}>Inactivo</span>
          )}
        </div>
      ),
    },
    {
      key: 'text_limit_weekly',
      title: 'Texto/semana',
      sortable: true,
      render: (v) => (
        <span className="text-sm font-mono" style={{ color: '#C8CDD9' }}>
          {v != null ? `${v.toLocaleString()} / sem` : '—'}
        </span>
      ),
    },
    {
      key: 'voice_limit_weekly',
      title: 'Voz/semana',
      sortable: true,
      render: (v) => (
        <span className="text-sm font-mono" style={{ color: '#C8CDD9' }}>
          {v != null ? `${v.toLocaleString()} / sem` : '—'}
        </span>
      ),
    },
    {
      key: 'id',
      title: 'Usuarios',
      sortable: false,
      render: (v) => {
        const count = userCounts[v] ?? 0;
        return (
          <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(59,130,246,0.12)', color: '#60A5FA' }}>
            {count} {count === 1 ? 'usuario' : 'usuarios'}
          </span>
        );
      },
    },
    {
      key: 'is_active',
      title: 'Estado',
      sortable: true,
      render: (v) => v
        ? <span className="text-xs font-medium text-green-400 flex items-center gap-1"><FaToggleOn size={14} /> Activo</span>
        : <span className="text-xs font-medium flex items-center gap-1" style={{ color: '#6B7391' }}><FaToggleOff size={14} /> Inactivo</span>,
    },
    {
      key: 'actions',
      title: '',
      sortable: false,
      render: (_, row) => (
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => handleEdit(row)}
            className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-colors"
            title="Editar"
          >
            <FaEdit size={14} />
          </button>
          {row.is_active && (
            <button
              onClick={() => handleDeactivate(row)}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: '#6B7391' }}
              title="Desactivar"
            >
              <FaToggleOn size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Planes de suscripción" subtitle="Configura límites y acceso por plan">
        <Button variant="warning" onClick={handleCreate} leftIcon={<FaPlus size={11} />}>
          Nuevo plan
        </Button>
      </PageHeader>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16,1,0.3,1] }}
        style={{
          background: 'linear-gradient(160deg, #161731 0%, #12132a 55%, #0f1024 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        {loading ? (
          <Skeleton.TableRows rows={5} cols={6} />
        ) : (
          <Table
            data={plans}
            columns={columns}
            options={{
              pagination: false,
              showSearch: false,
              emptyMessage: 'No hay planes configurados',
            }}
          />
        )}
      </motion.div>

      <PlanModal
        isOpen={modalOpen}
        editingPlan={editingPlan}
        onClose={() => setModalOpen(false)}
        onSaved={fetchPlans}
      />
    </div>
  );
};

export default AdminPlanesPage;
